import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';

const PrivacyPageEn = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoMeta
        title="Privacy Policy | BlueCMR"
        description="How BlueCMR S.L.U. processes your personal data: controller, purposes, legal bases, retention periods and how to exercise your GDPR rights."
        canonical="https://www.bluecmr.com/en/privacy"
        locale="en_GB"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-700">
            Information on the processing of your personal data
          </p>
          <p className="mt-4 text-sm text-gray-600">
            This English version is provided for information purposes. In the event of any discrepancy, the{' '}
            <Link to="/privacidad" className="text-blue-600 hover:underline">Spanish version</Link> shall prevail.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            <p>
              In this Privacy Policy we explain how <strong>BlueCMR S.L.U.</strong> processes your personal data when you visit our website, contact us, request information or demonstrations, use our support channels or interact with our services in those cases in which we act as controller.
            </p>
            <p>
              We also include a general explanation of the cases in which BlueCMR acts as processor or sub-processor on behalf of its clients in the context of providing the BlueCMR platform.
            </p>

            {/* 1. Data controller */}
            <h2>1. Data controller</h2>
            <p>The controller is:</p>
            <p>
              <strong>BlueCMR S.L.U.</strong><br />
              Tax ID (NIF): B26654517<br />
              Address: Calle Fueros de León 1, 24402, Ponferrada, León, Spain<br />
              Privacy contact email: <a href="mailto:contact@bluecmr.com">contact@bluecmr.com</a>
            </p>

            {/* 2. Scope of this Policy */}
            <h2>2. Scope of this Policy</h2>
            <p>This Policy applies to the processing of personal data carried out by BlueCMR when we act as controller, in particular in relation to:</p>
            <ul>
              <li>website visitors;</li>
              <li>persons who contact BlueCMR;</li>
              <li>persons requesting information, quotes, demonstrations or trials;</li>
              <li>clients and their contact persons;</li>
              <li>support and customer service users;</li>
              <li>persons who receive communications from BlueCMR;</li>
              <li>persons who interact with our corporate profiles or communication channels.</li>
            </ul>
            <p>
              This Policy does not replace the information that BlueCMR clients must provide to the users, signatories, drivers, consignees or other third parties whose data are processed through the platform when BlueCMR acts on behalf of those clients as processor.
            </p>

            {/* 3. Categories of data we may process */}
            <h2>3. Categories of data we may process</h2>
            <p>Depending on your relationship with BlueCMR, we may process the following categories of data:</p>

            <h3>a) Identification and contact data</h3>
            <ul>
              <li>first name and surname(s);</li>
              <li>company;</li>
              <li>position or role;</li>
              <li>email address;</li>
              <li>telephone number;</li>
              <li>postal address, where applicable.</li>
            </ul>

            <h3>b) Professional and commercial data</h3>
            <ul>
              <li>sector or activity;</li>
              <li>information relating to your interest in our products or services;</li>
              <li>information included in requests, forms, meetings, demonstrations or commercial communications.</li>
            </ul>

            <h3>c) Technical and usage data</h3>
            <ul>
              <li>IP address;</li>
              <li>session identifiers;</li>
              <li>date and time of access;</li>
              <li>technical logs;</li>
              <li>browser or device information;</li>
              <li>information necessary for technical support, security or incident diagnosis.</li>
            </ul>

            <h3>d) Browsing data</h3>
            <ul>
              <li>data obtained through cookies or similar technologies, in accordance with the applicable Cookie Policy.</li>
            </ul>

            <h3>e) Data included in communications</h3>
            <ul>
              <li>content of messages sent by email, forms, chat, instant messaging or other channels enabled by BlueCMR.</li>
            </ul>

            {/* 4. How we obtain your data */}
            <h2>4. How we obtain your data</h2>
            <p>We may obtain your personal data:</p>
            <ul>
              <li>directly from you, when you fill in forms, request a demo, contact BlueCMR or contract our services;</li>
              <li>during the contractual or pre-contractual relationship with BlueCMR;</li>
              <li>through the use of the website or our digital channels;</li>
              <li>through third parties where there is a sufficient legal basis for doing so, for example, within a legitimate business relationship or where you have authorised such disclosure.</li>
            </ul>

            {/* 5. Purposes and legal bases of the processing */}
            <h2>5. Purposes and legal bases of the processing</h2>

            <h3>5.1. Handling of requests for information, contact and demonstrations</h3>
            <p>We process your data to handle enquiries, requests for information, contact requests, meetings, demonstrations and, where applicable, steps prior to entering into a contract.</p>
            <p><strong>Legal basis:</strong> taking steps at the request of the data subject prior to entering into a contract and, where appropriate, legitimate interest in managing the business relationship.</p>
            <p><strong>Data processed:</strong> identification, contact and professional data, and the content of the request.</p>

            <h3>5.2. Client management and performance of the contractual relationship</h3>
            <p>We process your data to formalise, maintain and perform the contractual relationship with clients, including the administrative, operational, contractual and commercial management associated with the contracted services.</p>
            <p><strong>Legal basis:</strong> performance of the contract and compliance with legal obligations associated with the contractual relationship.</p>
            <p><strong>Data processed:</strong> identification, contact, professional and billing data, and data relating to the management of the business relationship.</p>

            <h3>5.3. Technical support and customer service</h3>
            <p>We process data to resolve incidents, provide technical or functional assistance, follow up on tickets, handle support enquiries and maintain traceability of the assistance provided.</p>
            <p><strong>Legal basis:</strong> performance of the contract and, in certain cases, legitimate interest in ensuring the security, continuity and proper provision of the services.</p>
            <p><strong>Data processed:</strong> identification and contact data, technical data, logs and information included in the support request.</p>

            <h3>5.4. Invoicing, accounting and collections management</h3>
            <p>We process the data necessary to issue invoices, manage payments, comply with accounting and tax obligations and, where applicable, manage collection issues.</p>
            <p><strong>Legal basis:</strong> performance of the contract and compliance with legal obligations.</p>
            <p><strong>Data processed:</strong> identification and contact data, billing data, and accounting and payment information.</p>

            <h3>5.5. Service-related communications</h3>
            <p>We process your data to send you communications necessary for the provision of the service, such as operational notices, incidents, relevant changes, contractual updates, security communications or essential information about the account or the service.</p>
            <p><strong>Legal basis:</strong> performance of the contract and legitimate interest in the proper management of the relationship with clients and contact users.</p>
            <p><strong>Data processed:</strong> identification and contact data.</p>

            <h3>5.6. Commercial communications</h3>
            <p>We may process your data to send you information about BlueCMR products, services, news, events, content or initiatives.</p>
            <p><strong>Legal basis:</strong> consent, where required, or legitimate interest in keeping clients or professional contacts informed about similar BlueCMR services, on the terms permitted by applicable law.</p>
            <p><strong>Data processed:</strong> identification and contact data, and data relating to the business relationship.</p>
            <p>You may object to this type of communication or withdraw your consent at any time, as applicable.</p>

            <h3>5.7. Security, fraud prevention and protection of the platform</h3>
            <p>We process certain data to protect our systems, prevent unauthorised access, investigate security incidents, detect misuse and preserve the integrity and availability of our services.</p>
            <p><strong>Legal basis:</strong> legitimate interest in ensuring the security of the information, the platform and the services, as well as compliance with legal obligations where applicable.</p>
            <p><strong>Data processed:</strong> technical data, logs, identifiers and, where applicable, data linked to incidents.</p>

            <h3>5.8. Compliance with legal obligations</h3>
            <p>We process your data where necessary to comply with legal obligations, respond to requests from authorities, and meet tax, accounting, commercial-law, data protection or any other legally enforceable obligations.</p>
            <p><strong>Legal basis:</strong> compliance with a legal obligation.</p>
            <p><strong>Data processed:</strong> only the data strictly necessary in each case.</p>

            {/* 6. Data retention */}
            <h2>6. Data retention</h2>
            <p>As a general rule, we will retain your data for as long as necessary to fulfil the purpose for which they were collected and, thereafter, for the applicable statutory retention and limitation periods.</p>
            <p>In particular:</p>
            <ul>
              <li><strong>requests for information, contact or demos:</strong> for the time necessary to handle them and, thereafter, for a reasonable period for commercial follow-up, unless you object or request erasure where applicable;</li>
              <li><strong>data of clients and contact persons:</strong> for the duration of the contractual relationship and, thereafter, for the applicable statutory periods relating to tax, accounting and contractual liability;</li>
              <li><strong>support data:</strong> for the duration of the relationship and, thereafter, for the time necessary to address any potential liabilities or to ensure the traceability of the assistance provided;</li>
              <li><strong>commercial communications:</strong> until you withdraw your consent or object to the processing, as applicable;</li>
              <li><strong>technical data and logs:</strong> for the period necessary for security, diagnosis, service continuity and regulatory compliance.</li>
            </ul>
            <p>When the data are no longer necessary, they will be erased, anonymised or retained in a duly blocked state where required by applicable law.</p>

            {/* 7. Recipients and categories of recipients */}
            <h2>7. Recipients and categories of recipients</h2>
            <p>Your data may be disclosed or made available to:</p>
            <ul>
              <li>technology and infrastructure providers;</li>
              <li>cloud hosting providers;</li>
              <li>email and communications providers;</li>
              <li>support, maintenance or security providers;</li>
              <li>legal, tax or accounting advisers, or auditors;</li>
              <li>financial institutions and payment service providers, where applicable;</li>
              <li>administrative, judicial or regulatory authorities, where there is a legal obligation or a valid request.</li>
            </ul>
            <p>These third parties will act, as applicable, as processors or as independent controllers.</p>
            <p>BlueCMR will contractually require its processors to act in accordance with applicable law and with appropriate instructions.</p>

            {/* 8. International data transfers */}
            <h2>8. International data transfers</h2>
            <p>As a general rule, BlueCMR endeavours to ensure that data processing takes place within the European Economic Area.</p>
            <p>Should it be necessary in any case to carry out an international data transfer outside the European Economic Area, such transfer will only take place on a valid basis under applicable law and with appropriate safeguards, such as standard contractual clauses or other legally accepted mechanisms.</p>

            {/* 9. Rights of data subjects */}
            <h2>9. Rights of data subjects</h2>
            <p>Where applicable, you may exercise the following rights:</p>
            <ul>
              <li>access;</li>
              <li>rectification;</li>
              <li>erasure;</li>
              <li>objection;</li>
              <li>restriction of processing;</li>
              <li>portability;</li>
              <li>withdrawal of consent, where the processing is based on consent;</li>
              <li>not to be subject to decisions based solely on automated processing which produce legal effects or similarly significant effects, in the cases provided for by law.</li>
            </ul>
            <p>To exercise your rights you may write to:</p>
            <p><a href="mailto:contact@bluecmr.com">contact@bluecmr.com</a></p>
            <p>or by post to:</p>
            <p>
              BlueCMR S.L.U.<br />
              Calle Fueros de León 1, 24402, Ponferrada, León, Spain<br />
              Ref.: &quot;Data Protection&quot;
            </p>
            <p>We may ask you for reasonable information to verify your identity.</p>
            <p>Furthermore, if you consider that the processing does not comply with applicable law, you may lodge a complaint with the <a href="https://www.aepd.es" target="_blank" rel="noopener noreferrer">Agencia Española de Protección de Datos (AEPD, the Spanish Data Protection Authority)</a>.</p>

            {/* 10. Mandatory or optional nature of the data */}
            <h2>10. Mandatory or optional nature of the data</h2>
            <p>Where the collection of certain data is necessary to handle a request, provide a service or maintain a contractual relationship, failure to provide such data may prevent us from handling your request or providing the corresponding service.</p>

            {/* 11. Communications and social media */}
            <h2>11. Communications and social media</h2>
            <p>BlueCMR may maintain a presence on social media or use messaging and digital communication channels. Your interactions with BlueCMR through such environments will also be subject to the privacy policies and terms of the relevant platform.</p>
            <p>BlueCMR will process the data you provide through those channels only to the extent necessary to respond to your enquiry, manage the relevant relationship or maintain the communication requested.</p>

            {/* 12. Processing carried out by BlueCMR as processor or sub-processor */}
            <h2>12. Processing carried out by BlueCMR as processor or sub-processor</h2>
            <p>In the context of providing the BlueCMR platform, BlueCMR commonly processes personal data on behalf of its clients, who are the ones that determine the main purposes and means of the processing with regard to the data entered into the platform in the course of their operations.</p>
            <p>In these cases:</p>
            <ul>
              <li>BlueCMR acts as processor or, where applicable, as sub-processor;</li>
              <li>the BlueCMR client acts as controller or, where applicable, as principal processor;</li>
              <li>BlueCMR processes the data in accordance with the applicable contract, the DPA and the client’s documented instructions;</li>
              <li>it is the client’s responsibility to fulfil its information obligations and to ensure the lawfulness of the processing it carries out through the platform.</li>
            </ul>
            <p>This may include, among others, processing relating to:</p>
            <ul>
              <li>transport documents;</li>
              <li>data of drivers, consignees, consignors or signatories;</li>
              <li>electronic evidence;</li>
              <li>point-in-time geolocation data associated with events in the transport operation (for example, the position of the driver’s device at the moment a delivery is confirmed, with its accuracy in metres, as evidence of the place and time of the delivery);</li>
              <li>support linked to the client’s operations;</li>
              <li>technical and operational records necessary for the provision of the service.</li>
            </ul>
            <p>Where the platform allows the position of the device to be recorded when a delivery is confirmed, this capture is a one-off (it does not involve continuous location tracking), is visibly displayed to the user before confirming, and is subject to the device’s location permission: if permission is denied, the delivery confirmation is still recorded, without a position. These data are retained together with the transport document into which they are incorporated and for the same periods applicable to that document.</p>
            <p>In these cases, this Policy is merely general and informative in scope. The main information about such processing must be provided by the relevant controller.</p>

            {/* 13. Electronic signature and signature data */}
            <h2>13. Electronic signature and signature data</h2>
            <p>BlueCMR may provide technical means for the capture, storage, preservation or management of electronic evidence and of certain data associated with signature processes, always within the framework of the service contracted by its clients.</p>
            <p>When such data are processed on behalf of a BlueCMR client, BlueCMR will act in accordance with the instructions of the relevant controller and the applicable DPA, without using such data for its own purposes incompatible with the provision of the service.</p>
            <p>BlueCMR will not use the signature data processed on behalf of its clients for its own purposes of biometric identification, profiling, model training or commercial reuse.</p>
            <p>Determining the applicable legal basis, informing signatories and ensuring the validity of the processing in each case will be the responsibility of the controller using the platform, without prejudice to BlueCMR’s own obligations as technology provider and processor.</p>

            {/* 14. Security measures */}
            <h2>14. Security measures</h2>
            <p>BlueCMR applies appropriate technical and organisational measures to protect personal data against destruction, loss, alteration, unauthorised access or improper disclosure, taking into account the nature of the data, the context of the processing and the existing risks.</p>
            <p>However, no system is completely invulnerable, so absolute security cannot be guaranteed.</p>

            {/* 15. Changes to this Policy */}
            <h2>15. Changes to this Policy</h2>
            <p>BlueCMR may update this Privacy Policy to adapt it to regulatory, interpretative, technical or operational changes.</p>
            <p>Where the changes are significant, we will communicate them by reasonable means. In any event, the version in force will be the one published at any given time.</p>

            <p className="mt-8"><strong>Version:</strong> July 2026</p>

          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPageEn;
